import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, ScrollText, Send, ThumbsUp, X, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const SEV = { hard: { cls: "sev-blocking", icon: "🛑", label: "Blocking" },
              soft: { cls: "sev-warning", icon: "⚠", label: "Warning" },
              advisory: { cls: "sev-info", icon: "ℹ", label: "Advisory" } };

const HUMAN_FIELD = {
  wind_speed_knots: "wind speed", wind_gust_knots: "wind gust",
  vessel_loa_m: "vessel LOA", vessel_draft_m: "vessel draft",
  passenger_count: "passenger count", personnel_count: "personnel on duty",
  asset_count: "assigned assets", door_distance_m: "asset-to-door distance",
  gangway_reach_m: "gangway reach", gangway_slope_deg: "gangway slope",
  gangway_alignment_angle_deg: "gangway quay-alignment angle",
  asset_separation_m: "asset center separation", asset_clearance_m: "asset edge clearance",
  asset_overlap_m: "asset footprint overlap",
  mooring_line_load_t: "mooring line load", mooring_line_length_m: "mooring line length",
  mooring_lead_angle_deg: "mooring lead angle", bollard_aggregate_load_t: "aggregate bollard load",
  bollard_load_utilization_pct: "bollard load utilization", bollard_line_count: "bollard line count",
};
const HUMAN_OP = { ">": "exceeds", ">=": "is at least", "<": "is below", "<=": "is at most", "==": "equals", "!=": "is not" };
const UNIT = { wind_speed_knots: "kn", wind_gust_knots: "kn", vessel_loa_m: "m", vessel_draft_m: "m",
  door_distance_m: "m", gangway_reach_m: "m", gangway_slope_deg: "°",
  gangway_alignment_angle_deg: "°", asset_separation_m: "m", asset_clearance_m: "m", asset_overlap_m: "m" };

Object.assign(UNIT, {
  mooring_line_load_t: "t", mooring_line_length_m: "m", mooring_lead_angle_deg: "\u00b0",
  bollard_aggregate_load_t: "t", bollard_load_utilization_pct: "%", bollard_line_count: "",
});

function humanize(c) {
  if (c?.all) return `All of: ${c.all.map(humanize).join("; ")}`;
  if (c?.any) return `Any of: ${c.any.map(humanize).join("; ")}`;
  if (c?.not) return `Not: ${humanize(c.not)}`;
  if (!c?.field) return "";
  return `When ${HUMAN_FIELD[c.field] || c.field} ${HUMAN_OP[c.op] || c.op} ${c.value}${UNIT[c.field] || ""}`;
}

export default function Sops() {
  const [rules, setRules] = useState([]);
  const [prompt, setPrompt] = useState("");
  const [cards, setCards] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [note, setNote] = useState("");

  const refresh = () => api.list("sop-rules").then(setRules);
  useEffect(() => { refresh(); }, []);

  const ask = async () => {
    if (!prompt.trim()) return;
    setParsing(true); setCards([]); setNote("");
    try {
      const r = await api.sopAgentParse({ prompt });
      setCards(r.cards || []);
      setNote(r.note || "");
      if (!r.cards?.length) toast.error("No rules could be derived. Try rephrasing.");
    } catch (e) { toast.error("Agent failed"); }
    finally { setParsing(false); }
  };

  const acceptCard = async (i) => {
    const c = cards[i];
    try {
      await api.sopAgentSaveCandidate(c);
      toast.success("Inactive advisory candidate saved for review");
      setCards(cards.filter((_, j) => j !== i));
      refresh();
    } catch (e) { toast.error("Accept failed"); }
  };
  const dropCard = (i) => setCards(cards.filter((_, j) => j !== i));
  const editCard = (i, key, val) => setCards(cards.map((c, j) => j === i ? { ...c, [key]: val } : c));

  const remove = async (rule) => { await api.remove("sop-rules", rule.id, rule.revision); refresh(); };

  const examples = [
    "Suspend bunkering operations whenever wind exceeds 35 knots.",
    "Require at least 2 personnel on site for any bunker operation.",
    "Vessels longer than 300m must have a pilot on board.",
    "Crane work is prohibited if wind gusts exceed 40 knots.",
    "Gangway slope must not exceed 25 degrees.",
    "Forklifts must maintain 3 metres edge clearance from gangways.",
  ];

  return (
    <div className="space-y-6">
      <div>
        <div className="font-mono text-xs uppercase tracking-widest text-slate-500 flex items-center gap-2">
          <Sparkles size={11} className="text-sky-400" /> Rule Authoring
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter text-slate-50">SOP Rules</h1>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1"><div className="font-mono text-[11px] text-slate-500">Describe procedures in plain language — AI cards are saved only as inactive advisory candidates for owner review.</div><Link to="/help#sop-rules" className="text-xs text-sky-300 hover:text-sky-200">SOP rule guide</Link></div>
      </div>

      {/* NL chat */}
      <div className="panel p-5">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-2">Describe guidance for review</div>
        <div className="flex gap-2">
          <Input
            data-testid="sop-agent-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
            placeholder="e.g. Suspend bunkering when wind is above 35 knots and require 2 safety-certified personnel on site"
            className="bg-[#050B14] border-[#1E293B] rounded-none flex-1"
          />
          <Button data-testid="btn-sop-agent-ask" onClick={ask} disabled={parsing || !prompt.trim()} className="bg-sky-600 hover:bg-sky-500 rounded-none">
            <Send size={14} className={`mr-1.5 ${parsing ? "animate-pulse" : ""}`} /> {parsing ? "Thinking…" : "Ask"}
          </Button>
        </div>
        {!cards.length && !parsing && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {examples.map((e) => (
              <button key={e} onClick={() => setPrompt(e)} className="text-[10px] font-mono px-2 py-1 bg-[#050B14] border border-[#1E293B] text-slate-400 hover:text-sky-300 hover:border-sky-500/40">{e}</button>
            ))}
          </div>
        )}
        {note && <div className="text-[10px] font-mono text-slate-500 mt-2 italic">Agent note: {note}</div>}
      </div>

      {/* Proposed cards */}
      {cards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="sop-agent-cards">
          {cards.map((c, i) => {
            const sev = SEV[c.category] || SEV.soft;
            return (
              <div key={i} className={`panel p-4 border-l-4 ${sev.cls}`} data-testid={`sop-card-${i}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className={`font-mono text-[10px] uppercase px-2 py-0.5 tracking-widest ${sev.cls}`}>{sev.icon} {sev.label}</span>
                </div>
                <Input value={c.name} onChange={(e) => editCard(i, "name", e.target.value)} className="bg-[#050B14] border-[#1E293B] rounded-none font-heading text-base mb-2" />
                <textarea
                  value={c.description}
                  onChange={(e) => editCard(i, "description", e.target.value)}
                  rows={3}
                  className="w-full bg-[#050B14] border border-[#1E293B] p-2 text-sm text-slate-200 mb-2"
                />
                <div className="font-mono text-[10px] text-slate-500 mb-1">Trigger: {humanize(c.condition)}</div>
                {Object.keys(c.applies_to || {}).length > 0 && <div className="font-mono text-[10px] text-slate-500 mb-3">Applies to: {JSON.stringify(c.applies_to)}</div>}
                <div className="flex gap-2">
                  <Button data-testid={`accept-sop-card-${i}`} onClick={() => acceptCard(i)} className="bg-emerald-600 hover:bg-emerald-500 rounded-none"><ThumbsUp size={12} className="mr-1.5" /> Save for owner review</Button>
                  <Button data-testid={`discard-sop-card-${i}`} onClick={() => dropCard(i)} variant="secondary" className="bg-[#162032] hover:bg-[#1E2B41] border border-[#1E293B] rounded-none text-slate-200"><X size={12} className="mr-1.5" /> Discard</Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Existing rules — humanized */}
      <div className="panel p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono mb-3">SOP records · {rules.length}</div>
        {rules.length === 0 && <div className="text-sm text-slate-500">No SOP records yet. AI suggestions remain inactive until an owner reviews and publishes guidance.</div>}
        <div className="space-y-2">
          {rules.map((r) => {
            const sev = SEV[r.category] || SEV.soft;
            return (
              <div key={r.id} className={`panel-2 p-3 border-l-4 ${sev.cls} flex items-start gap-3`} data-testid={`sop-row-${r.id}`}>
                <span className={`font-mono text-[10px] uppercase px-2 py-0.5 tracking-widest ${sev.cls} flex-shrink-0`}>{sev.icon} {sev.label}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <ScrollText size={12} className="text-sky-400" />
                    <span className="font-medium text-slate-100">{r.name}</span>
                    <span className="font-mono text-[10px] text-slate-500">v{r.version || 1}</span>
                    {r.review_status === "proposed" && <span className="font-mono text-[10px] text-amber-300">Review candidate · inactive</span>}
                  </div>
                  <div className="text-sm text-slate-300 mt-1">{r.description}</div>
                  <div className="font-mono text-[10px] text-slate-500 mt-1">{humanize(r.condition)}</div>
                  {Object.keys(r.applies_to || {}).length > 0 && <div className="font-mono text-[10px] text-slate-500 mt-1">Applies to: {JSON.stringify(r.applies_to)}</div>}
                  {(r.required_actions || []).map(action => <div key={action} className="text-xs text-amber-300 mt-1">Required: {action}</div>)}
                  {r.acknowledgement_required && <div className="font-mono text-[10px] text-sky-300 mt-1">Operator acknowledgement required</div>}
                  {(r.exemptions || []).length > 0 && <div className="font-mono text-[10px] text-slate-500 mt-1">Exemptions: {r.exemptions.length}</div>}
                </div>
                <button data-testid={`del-sop-${r.id}`} onClick={() => remove(r)} className="text-slate-500 hover:text-red-400 flex-shrink-0"><Trash2 size={12} /></button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
